// InstallPrompt — captures the browser's beforeinstallprompt event and
// surfaces a small "install this app" banner. Installed PWAs:
//   - launch from the home screen / dock instead of a browser tab,
//   - get their own session cookie jar (won't get logged out when the
//     operator clears Safari history),
//   - support more reliable push notifications + offline persistence.
//
// Behaviour:
//   - On Chrome / Edge / Samsung Internet, beforeinstallprompt fires
//     when PWA criteria are met; we save the event and render the
//     banner. Tap "Install" → event.prompt() → native install dialog.
//   - On iOS Safari, beforeinstallprompt isn't supported; we render a
//     hint instead telling the user to use Share → Add to Home Screen.
//   - Dismissed banner stays hidden for 14 days. If the user changes
//     their mind sooner they can clear localStorage or use the browser
//     menu directly.
//   - If the app is already installed (display-mode standalone), the
//     component renders nothing.

import { useEffect, useState } from 'react'
import { Download, X, Share } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'install_prompt_dismissed_at'
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000  // 14 days

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS-specific.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone
  return iosStandalone === true
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS  = /iPad|iPhone|iPod/.test(ua)
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

function recentlyDismissed(): boolean {
  if (typeof window === 'undefined') return false
  const v = window.localStorage.getItem(DISMISS_KEY)
  if (!v) return false
  const ts = parseInt(v, 10)
  if (Number.isNaN(ts)) return false
  return Date.now() - ts < DISMISS_TTL_MS
}

export default function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState(recentlyDismissed)
  const [showIosHint, setShowIosHint] = useState(false)

  useEffect(() => {
    if (isStandalone()) return  // Already installed — nothing to do.

    const onInstall = (e: Event) => {
      e.preventDefault()
      setEvent(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onInstall)

    // iOS Safari hint — show once per device (no beforeinstallprompt
    // on iOS, the user has to use Share → Add to Home Screen manually).
    if (isIosSafari() && !recentlyDismissed()) setShowIosHint(true)

    return () => window.removeEventListener('beforeinstallprompt', onInstall)
  }, [])

  const dismiss = () => {
    setDismissed(true)
    setEvent(null)
    setShowIosHint(false)
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* private mode */ }
  }

  const install = async () => {
    if (!event) return
    await event.prompt()
    const outcome = await event.userChoice
    setEvent(null)
    if (outcome.outcome === 'accepted') {
      try { window.localStorage.removeItem(DISMISS_KEY) } catch { /* ignore */ }
    } else {
      dismiss()
    }
  }

  if (dismissed) return null
  if (isStandalone()) return null

  // Chrome / Edge / Samsung path.
  if (event) {
    return (
      <aside className="print:hidden fixed bottom-3 right-3 left-3 sm:left-auto sm:max-w-sm z-chrome surface-sunk border hairline-strong rounded-2xl shadow-xl p-3 flex items-start gap-3">
        <Download size={18} className="text-blue-400 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-white light:text-fg">Install the app</p>
          <p className="text-xs text-fg-muted mt-0.5">
            Add the packhouse app to your home screen for faster access and offline
            support during pack season.
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={install} className="btn-primary btn-sm">Install</button>
            <button onClick={dismiss} className="btn-ghost btn-sm">Not now</button>
          </div>
        </div>
        <button onClick={dismiss} title="Dismiss" aria-label="Dismiss the install prompt"
          className="min-w-[44px] min-h-[44px] -m-[11px] inline-flex items-center justify-center rounded-lg hover:bg-surface text-fg-muted hover:text-white shrink-0">
          <X size={14} />
        </button>
      </aside>
    )
  }

  // iOS Safari path.
  if (showIosHint) {
    return (
      <aside className="print:hidden fixed bottom-3 right-3 left-3 sm:left-auto sm:max-w-sm z-chrome surface-sunk border hairline-strong rounded-2xl shadow-xl p-3 flex items-start gap-3">
        <Share size={18} className="text-blue-400 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-white light:text-fg">Add to home screen</p>
          <p className="text-xs text-fg-muted mt-0.5">
            Tap the Share icon at the bottom of Safari, then "Add to Home Screen"
            to install the packhouse app.
          </p>
          <button onClick={dismiss} className="btn-ghost btn-sm mt-2">Got it</button>
        </div>
        <button onClick={dismiss} title="Dismiss" aria-label="Dismiss the install prompt"
          className="min-w-[44px] min-h-[44px] -m-[11px] inline-flex items-center justify-center rounded-lg hover:bg-surface text-fg-muted hover:text-white shrink-0">
          <X size={14} />
        </button>
      </aside>
    )
  }

  return null
}
